/**
 * lib/bursPdf.ts
 * ------------------------------------------------------------------
 * BURS STATUTORY FORMS — PDF renderers (clean compliant layout).
 *
 * Mirrors lib/payslipPdf.ts (jsPDF, LKC letterhead, navy palette,
 * compliance disclaimer footer). These are not pixel facsimiles of
 * the official BURS stationery; they are clean, compliant layouts
 * carrying every required field, which BURS accepts and which is
 * robust to the agency tweaking its template.
 *
 *   buildItw7Pdf   — monthly PAYE return (one month)
 *   buildItw8Pdf   — employee annual tax certificate(s)
 *   buildItw10Pdf  — annual PAYE reconciliation (one tax year)
 *
 * .output('blob') the result to upload/email, or .save() to download.
 * ------------------------------------------------------------------ */
import { jsPDF } from 'jspdf';
import { SCHOOL } from '@/lib/school.config';
import { fmtPula } from '@/lib/hrPayroll';
import type {
  MonthlyReturn,
  AnnualReconciliation,
  EmployeeAnnualRow,
  TaxYear,
  BursEmployerDetails,
} from '@/lib/bursForms';

const NAVY: [number, number, number] = [27, 58, 107];
const INK: [number, number, number] = [31, 41, 55];
const MUTED: [number, number, number] = [107, 114, 128];
const RULE: [number, number, number] = [209, 213, 219];
const ZEBRA: [number, number, number] = [243, 246, 250];

const A4_W = 210;
const A4_H = 297;
const MX = 16;
const BODY_BOTTOM = A4_H - 22; // leave room for the footer

const DISCLAIMER =
  'Figures are aggregated from generated payslips for the period shown, computed using Botswana tax tables. The employer is responsible for verifying these figures against current BURS regulations before submission. Acepsle Pty Ltd makes no representation as to the accuracy of statutory deductions.';

export interface BursPdfOptions {
  logoDataUrl?: string | null;
  /** Who generated it (printed in the footer for the audit trail). */
  preparedBy?: string;
}

function setText(pdf: jsPDF, c: [number, number, number]) { pdf.setTextColor(c[0], c[1], c[2]); }
function setDraw(pdf: jsPDF, c: [number, number, number]) { pdf.setDrawColor(c[0], c[1], c[2]); }
function setFill(pdf: jsPDF, c: [number, number, number]) { pdf.setFillColor(c[0], c[1], c[2]); }

function crest(pdf: jsPDF, cx: number, cy: number, r: number, logo?: string | null) {
  if (logo) {
    try { pdf.addImage(logo, 'PNG', cx - r, cy - r, r * 2, r * 2); return; } catch { /* fall through */ }
  }
  setDraw(pdf, NAVY); pdf.setLineWidth(0.6); pdf.circle(cx, cy, r, 'S');
  setText(pdf, NAVY); pdf.setFont('helvetica', 'bold'); pdf.setFontSize(r * 0.8);
  pdf.text('LKC', cx, cy + r * 0.25, { align: 'center' });
}

/** Shared letterhead. Returns the next free y. */
function letterhead(pdf: jsPDF, opt: BursPdfOptions): number {
  const cx = A4_W / 2;
  let y = 16;
  crest(pdf, MX + 8, y + 4, 9, opt.logoDataUrl);
  setText(pdf, NAVY); pdf.setFont('helvetica', 'bold'); pdf.setFontSize(15);
  pdf.text(SCHOOL.name, cx, y, { align: 'center' });
  setText(pdf, MUTED); pdf.setFont('helvetica', 'normal'); pdf.setFontSize(8.5);
  pdf.text(SCHOOL.contact.addressLines.join(', '), cx, y + 5.5, { align: 'center' });
  pdf.text(`Tel: ${SCHOOL.contact.phonePrimary}`, cx, y + 10, { align: 'center' });
  y += 16;
  setDraw(pdf, NAVY); pdf.setLineWidth(0.5); pdf.line(MX, y, A4_W - MX, y);
  return y + 8;
}

/** Form title + a right-aligned period/scope line. */
function titleBlock(pdf: jsPDF, y: number, code: string, name: string, scope: string): number {
  setText(pdf, INK); pdf.setFont('helvetica', 'bold'); pdf.setFontSize(12.5);
  pdf.text(`${code} — ${name}`, MX, y);
  setText(pdf, MUTED); pdf.setFont('helvetica', 'normal'); pdf.setFontSize(9);
  pdf.text(scope, A4_W - MX, y, { align: 'right' });
  return y + 7;
}

/** Employer details box (name, TIN, address). Returns next free y. */
function employerBlock(pdf: jsPDF, y: number, emp: BursEmployerDetails): number {
  const h = 7 + Math.max(2, emp.addressLines.length) * 4.4 + 3;
  setFill(pdf, ZEBRA); setDraw(pdf, RULE); pdf.setLineWidth(0.3);
  pdf.roundedRect(MX, y, A4_W - MX * 2, h, 2, 2, 'FD');
  let yy = y + 5;
  setText(pdf, MUTED); pdf.setFont('helvetica', 'bold'); pdf.setFontSize(7);
  pdf.text('EMPLOYER', MX + 4, yy);
  setText(pdf, INK); pdf.setFont('helvetica', 'bold'); pdf.setFontSize(9.5);
  pdf.text(emp.name, MX + 30, yy);
  pdf.setFont('helvetica', 'normal'); pdf.setFontSize(8.5);
  pdf.text(`Employer TIN: ${emp.tin || '________________'}`, A4_W - MX - 4, yy, { align: 'right' });
  yy += 5;
  setText(pdf, MUTED); pdf.setFont('helvetica', 'normal'); pdf.setFontSize(8);
  for (const line of emp.addressLines) { pdf.text(line, MX + 30, yy); yy += 4.4; }
  return y + h + 5;
}

interface Col {
  header: string;
  /** Width in mm. */
  w: number;
  align: 'left' | 'right';
  /** Optional cell value extractor for money columns (formats BWP). */
  money?: boolean;
}

/** Compute the left x of each column from the table's left edge. */
function colXs(cols: Col[], left: number): number[] {
  const xs: number[] = [];
  let x = left;
  for (const c of cols) { xs.push(x); x += c.w; }
  return xs;
}

function tableHead(pdf: jsPDF, y: number, cols: Col[], left: number): number {
  const totalW = cols.reduce((s, c) => s + c.w, 0);
  const xs = colXs(cols, left);
  setFill(pdf, NAVY); pdf.rect(left, y, totalW, 7, 'F');
  setText(pdf, [255, 255, 255]); pdf.setFont('helvetica', 'bold'); pdf.setFontSize(7.5);
  cols.forEach((c, i) => {
    const tx = c.align === 'right' ? xs[i] + c.w - 2 : xs[i] + 2;
    pdf.text(c.header.toUpperCase(), tx, y + 4.7, { align: c.align });
  });
  return y + 7;
}

/**
 * Generic paginated table. `rows` are already-formatted cell strings.
 * Repeats the column header + letterhead on overflow. Returns the y
 * after the last row (no totals — caller draws those).
 */
function tableBody(
  pdf: jsPDF,
  startY: number,
  cols: Col[],
  rows: string[][],
  opt: BursPdfOptions,
  redrawHead: (pdf: jsPDF) => number,
): number {
  const totalW = cols.reduce((s, c) => s + c.w, 0);
  const xs = colXs(cols, MX);
  let y = startY;
  setText(pdf, INK); pdf.setFontSize(8);
  rows.forEach((cells, idx) => {
    if (y + 5.5 > BODY_BOTTOM) {
      footer(pdf, opt);
      pdf.addPage();
      const ly = redrawHead(pdf);
      y = tableHead(pdf, ly, cols, MX);
      setText(pdf, INK); pdf.setFontSize(8);
    }
    if (idx % 2 === 1) { setFill(pdf, ZEBRA); pdf.rect(MX, y, totalW, 5.2, 'F'); }
    setText(pdf, INK); pdf.setFont('helvetica', 'normal');
    cells.forEach((val, i) => {
      const tx = cols[i].align === 'right' ? xs[i] + cols[i].w - 2 : xs[i] + 2;
      pdf.text(val, tx, y + 3.7, { align: cols[i].align, maxWidth: cols[i].w - 3 });
    });
    y += 5.2;
  });
  setDraw(pdf, RULE); pdf.setLineWidth(0.3); pdf.line(MX, y + 0.5, MX + totalW, y + 0.5);
  return y + 0.5;
}

function totalsRow(pdf: jsPDF, y: number, cols: Col[], cells: string[]): number {
  const totalW = cols.reduce((s, c) => s + c.w, 0);
  const xs = colXs(cols, MX);
  setText(pdf, NAVY); pdf.setFont('helvetica', 'bold'); pdf.setFontSize(8.5);
  cells.forEach((val, i) => {
    if (!val) return;
    const tx = cols[i].align === 'right' ? xs[i] + cols[i].w - 2 : xs[i] + 2;
    pdf.text(val, tx, y + 4.5, { align: cols[i].align });
  });
  void totalW;
  return y + 7;
}

function footer(pdf: jsPDF, opt: BursPdfOptions): void {
  const fy = A4_H - 18;
  setDraw(pdf, RULE); pdf.setLineWidth(0.3); pdf.line(MX, fy, A4_W - MX, fy);
  setText(pdf, MUTED); pdf.setFont('helvetica', 'italic'); pdf.setFontSize(6.6);
  const lines = pdf.splitTextToSize(DISCLAIMER, A4_W - MX * 2);
  pdf.text(lines, MX, fy + 3.5);
  const stamp = `Generated ${new Date().toLocaleDateString()}${opt.preparedBy ? ` · by ${opt.preparedBy}` : ''}`;
  setText(pdf, MUTED); pdf.setFont('helvetica', 'normal'); pdf.setFontSize(6.6);
  pdf.text(stamp, A4_W - MX, A4_H - 6, { align: 'right' });
}

/* ==================================================================
   ITW-7 — MONTHLY PAYE RETURN
   ================================================================== */

export function buildItw7Pdf(
  ret: MonthlyReturn,
  emp: BursEmployerDetails,
  opt: BursPdfOptions = {},
): jsPDF {
  const pdf = new jsPDF({ unit: 'mm', format: 'a4' });
  const head = (p: jsPDF): number => {
    let y = letterhead(p, opt);
    y = titleBlock(p, y, 'ITW-7', 'Monthly PAYE Return', ret.monthLabel);
    return employerBlock(p, y, emp);
  };
  let y = head(pdf);

  const cols: Col[] = [
    { header: 'Emp No', w: 22, align: 'left' },
    { header: 'Employee', w: ret.hasGratuityTax ? 58 : 78, align: 'left' },
    { header: 'Gross Emoluments', w: 33, align: 'right' },
    { header: 'PAYE', w: 30, align: 'right' },
    ...(ret.hasGratuityTax ? [{ header: 'Lump-sum Tax', w: 20, align: 'right' as const }] : []),
    { header: 'Total Tax', w: 30, align: 'right' },
  ];
  y = tableHead(pdf, y, cols, MX);
  const rows = ret.rows.map((r) => [
    `#${r.employeeNo}`,
    r.name,
    fmtPula(r.gross),
    fmtPula(r.paye),
    ...(ret.hasGratuityTax ? [fmtPula(r.gratuityTax)] : []),
    fmtPula(r.totalTax),
  ]);
  y = tableBody(pdf, y, cols, rows, opt, head);
  y = totalsRow(pdf, y, cols, [
    '',
    `Total — ${ret.totals.employees} employees`,
    fmtPula(ret.totals.gross),
    fmtPula(ret.totals.paye),
    ...(ret.hasGratuityTax ? [fmtPula(ret.totals.gratuityTax)] : []),
    fmtPula(ret.totals.totalTax),
  ]);

  // Remittance summary box
  y += 4;
  setFill(pdf, ZEBRA); setDraw(pdf, RULE); pdf.setLineWidth(0.3);
  pdf.roundedRect(MX, y, A4_W - MX * 2, 14, 2, 2, 'FD');
  setText(pdf, INK); pdf.setFont('helvetica', 'bold'); pdf.setFontSize(10);
  pdf.text('Total tax due to BURS this month', MX + 4, y + 8.5);
  setText(pdf, NAVY); pdf.setFontSize(12);
  pdf.text(`BWP ${fmtPula(ret.totals.totalTax)}`, A4_W - MX - 4, y + 8.5, { align: 'right' });

  footer(pdf, opt);
  return pdf;
}

/* ==================================================================
   ITW-8 — EMPLOYEE ANNUAL TAX CERTIFICATE
   ================================================================== */

function renderItw8Cert(
  pdf: jsPDF,
  row: EmployeeAnnualRow,
  ty: TaxYear,
  emp: BursEmployerDetails,
  opt: BursPdfOptions,
): void {
  let y = letterhead(pdf, opt);
  y = titleBlock(pdf, y, 'ITW-8', "Employee's Tax Deduction Certificate", ty.label);
  y = employerBlock(pdf, y, emp);

  // Employee block
  setFill(pdf, ZEBRA); setDraw(pdf, RULE); pdf.setLineWidth(0.3);
  pdf.roundedRect(MX, y, A4_W - MX * 2, 22, 2, 2, 'FD');
  const colR = A4_W / 2 + 6;
  setText(pdf, MUTED); pdf.setFont('helvetica', 'normal'); pdf.setFontSize(7);
  pdf.text('EMPLOYEE', MX + 4, y + 5);
  pdf.text('EMPLOYEE NO', colR, y + 5);
  setText(pdf, INK); pdf.setFont('helvetica', 'bold'); pdf.setFontSize(10);
  pdf.text(row.name, MX + 4, y + 10);
  pdf.text(`#${row.employeeNo}`, colR, y + 10);
  setText(pdf, MUTED); pdf.setFont('helvetica', 'normal'); pdf.setFontSize(7);
  pdf.text('DESIGNATION', MX + 4, y + 15);
  pdf.text('EMPLOYEE TIN', colR, y + 15);
  setText(pdf, INK); pdf.setFont('helvetica', 'normal'); pdf.setFontSize(8.5);
  pdf.text(row.jobTitle || '—', MX + 4, y + 19.5);
  pdf.text('________________', colR, y + 19.5);
  y += 28;

  // Figures
  const figs: [string, string][] = [
    ['Months paid in tax year', String(row.monthsPaid)],
    ['Gross emoluments', `BWP ${fmtPula(row.gross)}`],
    ['PAYE deducted', `BWP ${fmtPula(row.paye)}`],
    ...(row.gratuityTax > 0 ? [['Lump-sum / gratuity tax', `BWP ${fmtPula(row.gratuityTax)}`] as [string, string]] : []),
    ['Total tax deducted', `BWP ${fmtPula(row.totalTax)}`],
  ];
  setDraw(pdf, RULE); pdf.setLineWidth(0.3);
  for (const [label, val] of figs) {
    const bold = label === 'Total tax deducted';
    setText(pdf, bold ? NAVY : INK);
    pdf.setFont('helvetica', bold ? 'bold' : 'normal'); pdf.setFontSize(bold ? 10 : 9);
    pdf.text(label, MX + 2, y);
    pdf.text(val, A4_W - MX - 2, y, { align: 'right' });
    y += 2.5; pdf.line(MX, y, A4_W - MX, y); y += 5.5;
  }

  // Declaration
  y += 4;
  setText(pdf, MUTED); pdf.setFont('helvetica', 'italic'); pdf.setFontSize(8);
  const decl = pdf.splitTextToSize(
    `This certifies that the above tax was deducted from the employee's emoluments for the tax year ${ty.label} and remitted to the Botswana Unified Revenue Service.`,
    A4_W - MX * 2,
  );
  pdf.text(decl, MX, y);
  y += decl.length * 4.5 + 10;
  setText(pdf, INK); pdf.setFont('helvetica', 'normal'); pdf.setFontSize(8.5);
  pdf.text('Authorised signature: ______________________', MX, y);
  pdf.text('Date: ____________', A4_W - MX, y, { align: 'right' });

  footer(pdf, opt);
}

/** Generate-all = one certificate page per employee, in order. */
export function buildItw8Pdf(
  rows: EmployeeAnnualRow[],
  ty: TaxYear,
  emp: BursEmployerDetails,
  opt: BursPdfOptions = {},
): jsPDF {
  const pdf = new jsPDF({ unit: 'mm', format: 'a4' });
  rows.forEach((row, i) => {
    if (i > 0) pdf.addPage();
    renderItw8Cert(pdf, row, ty, emp, opt);
  });
  return pdf;
}

/* ==================================================================
   ITW-10 — ANNUAL PAYE RECONCILIATION
   ================================================================== */

export function buildItw10Pdf(
  recon: AnnualReconciliation,
  emp: BursEmployerDetails,
  opt: BursPdfOptions = {},
): jsPDF {
  const pdf = new jsPDF({ unit: 'mm', format: 'a4' });
  const hasG = recon.totals.gratuityTax > 0;
  const head = (p: jsPDF): number => {
    let y = letterhead(p, opt);
    y = titleBlock(p, y, 'ITW-10', 'Annual PAYE Reconciliation', recon.taxYear.label);
    return employerBlock(p, y, emp);
  };
  let y = head(pdf);

  // Month-by-month table
  setText(pdf, INK); pdf.setFont('helvetica', 'bold'); pdf.setFontSize(9);
  pdf.text('Monthly summary', MX, y); y += 4;
  const mCols: Col[] = [
    { header: 'Month', w: 46, align: 'left' },
    { header: 'Employees', w: 26, align: 'right' },
    { header: 'Gross Emoluments', w: hasG ? 38 : 50, align: 'right' },
    { header: 'PAYE', w: 30, align: 'right' },
    ...(hasG ? [{ header: 'Lump-sum', w: 18, align: 'right' as const }] : []),
    { header: 'Total Tax', w: 30, align: 'right' },
  ];
  y = tableHead(pdf, y, mCols, MX);
  const mRows = recon.months.map((m) => [
    m.monthLabel,
    String(m.employees),
    fmtPula(m.gross),
    fmtPula(m.paye),
    ...(hasG ? [fmtPula(m.gratuityTax)] : []),
    fmtPula(m.totalTax),
  ]);
  y = tableBody(pdf, y, mCols, mRows, opt, head);
  y = totalsRow(pdf, y, mCols, [
    'Year total',
    String(recon.totals.employees),
    fmtPula(recon.totals.gross),
    fmtPula(recon.totals.paye),
    ...(hasG ? [fmtPula(recon.totals.gratuityTax)] : []),
    fmtPula(recon.totals.totalTax),
  ]);

  // Per-employee breakdown (own page if it won't fit)
  if (y + 30 > BODY_BOTTOM) { footer(pdf, opt); pdf.addPage(); y = head(pdf); }
  else y += 6;
  setText(pdf, INK); pdf.setFont('helvetica', 'bold'); pdf.setFontSize(9);
  pdf.text('Per-employee breakdown', MX, y); y += 4;
  const eCols: Col[] = [
    { header: 'Emp No', w: 22, align: 'left' },
    { header: 'Employee', w: hasG ? 56 : 66, align: 'left' },
    { header: 'Months', w: 18, align: 'right' },
    { header: 'Gross Emoluments', w: 34, align: 'right' },
    { header: 'PAYE', w: 26, align: 'right' },
    ...(hasG ? [{ header: 'Lump-sum', w: 18, align: 'right' as const }] : []),
    { header: 'Total Tax', w: 26, align: 'right' },
  ];
  y = tableHead(pdf, y, eCols, MX);
  const eRows = recon.byEmployee.map((r) => [
    `#${r.employeeNo}`,
    r.name,
    String(r.monthsPaid),
    fmtPula(r.gross),
    fmtPula(r.paye),
    ...(hasG ? [fmtPula(r.gratuityTax)] : []),
    fmtPula(r.totalTax),
  ]);
  y = tableBody(pdf, y, eCols, eRows, opt, head);
  totalsRow(pdf, y, eCols, [
    '',
    `Total — ${recon.totals.employees} employees`,
    '',
    fmtPula(recon.totals.gross),
    fmtPula(recon.totals.paye),
    ...(hasG ? [fmtPula(recon.totals.gratuityTax)] : []),
    fmtPula(recon.totals.totalTax),
  ]);

  footer(pdf, opt);
  return pdf;
}
